import type { LoanRow, LoanLedgerEntry } from './data.js';

/**
 * Shared loan display math used by the Loans and Assets routes.
 * Previously duplicated: `informalBalance` lived in routes/Loans.tsx and
 * `loanOutstanding` in routes/Assets.tsx — same outstanding-balance rule.
 */

/** Latest running balance of an informal loan (the last dated ledger row), 0 if empty. */
export function informalBalance(entries: LoanLedgerEntry[]): number {
  return entries.length ? (entries[entries.length - 1]?.running_balance_ore ?? 0) : 0;
}

/**
 * Outstanding balance of one loan (the rule both the Loans and Assets pages apply):
 *  - formal loans → the principal (restgæld nu),
 *  - informal loans → the magnitude of a negative running balance (the last dated ledger row).
 */
export function loanOutstanding(loan: LoanRow, entries: LoanLedgerEntry[]): number {
  if (loan.type !== 'informal') return loan.principal_ore;
  return Math.max(0, -informalBalance(entries));
}

/**
 * Total outstanding debt across every loan — the figure the Loans page shows.
 * Sums {@link loanOutstanding} per loan (formal → principal, informal →
 * magnitude of a negative balance), so the total obeys the same rule the
 * per-loan cards and the Assets page apply. `entriesByLoanId` maps loan id to
 * its ledger rows; a loan with no entry contributes its formal principal or 0.
 */
export function totalOutstanding(
  loans: LoanRow[],
  entriesByLoanId: Map<string, LoanLedgerEntry[]>,
): number {
  return loans.reduce(
    (sum, loan) => sum + loanOutstanding(loan, entriesByLoanId.get(loan.id) ?? []),
    0,
  );
}

/** Add `months` to a 'YYYY-MM-DD' date, returning 'MM/YYYY' for a schedule axis label. */
export function scheduleDateLabel(startDate: string | null, period: number): string {
  const parts = (startDate ?? '2026-01-01').split('-');
  const y0 = Number(parts[0] ?? 2026);
  const m0 = Number(parts[1] ?? 1);
  const total = y0 * 12 + (m0 - 1) + (period - 1);
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return `${String(m).padStart(2, '0')}/${y}`;
}
